import {
  colors,
  containerNames,
  containers,
  containerScales,
  darkThemeSelector,
  devices,
  FocusRing,
  rem,
  selectors,
  shadows,
  sizing,
  styled,
  Text,
  UtilityControl,
  UtilityControlGroup,
  zIndexes,
} from '@meterup/atto';

const flexiColumnsZIndexes = {
  background: zIndexes.base + 1,
  foreground: zIndexes.base + 2,
  peek: zIndexes.base + 3,
  divider: zIndexes.base + 4,
};

export const FLEXI_COLUMNS_ANIMATION_MS = 200;

const FLEXI_COLUMNS_ROOT_CONTAINER_NAME = 'flexi-columns-root';
const rootNarrow = `@container ${FLEXI_COLUMNS_ROOT_CONTAINER_NAME} (max-width: ${containerScales.pane.narrow}px)`;

export const FLEXI_COLUMNS_RESIZE_BOUNDS = {
  min: 280,
  max: 720,
};

const opaqueControlStyles = {
  backgroundColor: colors.bgNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgNeutralDark,
  },
};

export const FlexiColumnsToggleButton = styled(UtilityControl, {
  position: 'absolute',
  minWidth: 0,

  [devices.touch]: {
    top: '$2',
  },

  [devices.mouse]: {
    top: '$8',
  },

  [containers.pane.narrow]: {
    '&&': {
      display: 'none',
    },
  },

  variants: {
    opaque: {
      true: {
        '&&': opaqueControlStyles,
      },
    },
    position: {
      start: {
        left: `$${sizing.primary}`,
        transform: 'translateX(-50%)',
      },
      end: {
        right: `$${sizing.primary}`,
        transform: 'translateX(50%)',
      },
    },
  },
});
FlexiColumnsToggleButton.displayName = 'FlexiColumnsToggleButton';

export const FlexiColumnsEdgeTab = styled('button', FocusRing, {
  display: 'none',
  position: 'absolute',
  top: '12%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$4',
  heightAll: '$108',
  padding: 0,
  overflow: 'hidden',
  cursor: 'pointer',
  border: `1px solid ${colors.strokeNeutralLight}`,
  backgroundColor: colors.bgSurfaceLight,
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    borderColor: colors.strokeNeutralDark,
    backgroundColor: colors.bgSurfaceDark,
    color: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    backgroundColor: colors.bgNeutralLight,
    color: colors.headingNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      color: colors.headingNeutralDark,
    },
  },

  [devices.mouse]: {
    widthAll: '$20',
  },

  [devices.touch]: {
    widthAll: '$24',
  },

  [containers.pane.narrow]: {
    display: 'flex',
  },

  variants: {
    // A nested `FlexiColumns` hands its edge tabs to the outermost one so they share a top line.
    hoistable: {
      true: {
        [rootNarrow]: {
          display: 'none',
        },
      },
    },
    position: {
      start: {
        left: 0,
        borderLeft: 'none',
        borderRadius: '0 $8 $8 0',
      },
      end: {
        right: 0,
        borderRight: 'none',
        borderRadius: '$8 0 0 $8',
      },
    },
  },
});
FlexiColumnsEdgeTab.displayName = 'FlexiColumnsEdgeTab';

export const FlexiColumnsEdgeTabLabel = styled(Text, {
  writingMode: 'vertical-rl',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: 0,
  color: 'inherit',
});
FlexiColumnsEdgeTabLabel.displayName = 'FlexiColumnsEdgeTabLabel';

const columnVisibleStyles = {
  zIndex: 2,

  [containers.pane.narrow]: {
    overflow: 'auto',
    transform: 'translateX(0)',
  },

  [containers.pane.notNarrow]: {
    minWidth: 0,
    overflow: 'auto',
  },
};

const columnHiddenStyles = {
  zIndex: flexiColumnsZIndexes.peek,

  [containers.pane.narrow]: {
    overflow: 'visible',
    boxShadow: shadows.overlayLight,
    transform: `translateX(calc(-100% + ${sizing.primary}px))`,

    [darkThemeSelector]: {
      boxShadow: shadows.overlayDark,
    },

    [selectors.focus]: {
      outline: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    overflow: 'hidden',
    minWidth: 0,
  },
};

export const FlexiColumnsColumn = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  [containers.pane.narrow]: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${FLEXI_COLUMNS_ANIMATION_MS}ms ease`,
    willChange: 'transform',
  },

  variants: {
    hasDivider: {
      true: {},
      false: {},
    },
    position: {
      start: {
        [containers.pane.notNarrow]: {
          gridArea: 'start',
          strokeRight: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeRight: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.foreground,
        },
      },
      end: {
        [containers.pane.notNarrow]: {
          gridArea: 'end',
          strokeLeft: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeLeft: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.background,
        },
      },
    },
    visible: {
      true: columnVisibleStyles,
      false: columnHiddenStyles,
    },
  },

  compoundVariants: [
    {
      position: 'start',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(-100%)',
        },
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(100%)',
        },
      },
    },
    {
      hasDivider: true,
      position: 'start',
      css: {
        [containers.pane.notNarrow]: {
          strokeRight: colors.transparent,

          [darkThemeSelector]: {
            strokeRight: colors.transparent,
          },
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      },
    },
    {
      hasDivider: true,
      position: 'end',
      css: {
        [containers.pane.notNarrow]: {
          strokeLeft: colors.transparent,

          [darkThemeSelector]: {
            strokeLeft: colors.transparent,
          },
        },
      },
    },
  ],
});
FlexiColumnsColumn.displayName = 'FlexiColumnsColumn';

export const FlexiColumnsColumnBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
});
FlexiColumnsColumnBody.displayName = 'FlexiColumnsColumnBody';

export const FlexiColumnsRail = styled('div', {
  display: 'none',
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.divider,
  justifyContent: 'center',
  // Clears the expand toggle above it.
  paddingTop: '$44',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: colors.bgSurfaceLight,
  color: colors.iconNeutralLight,
  // Fades in below the gap where the pane header meets its content, which no stroke crosses.
  maskImage: `linear-gradient(to bottom, transparent, black ${rem(44)})`,
  transition: 'background-color 120ms ease, color 120ms ease',

  [darkThemeSelector]: {
    backgroundColor: colors.bgSurfaceDark,
    color: colors.iconNeutralDark,
  },

  [containers.pane.notNarrow]: {
    display: 'flex',
  },

  [`${selectors.hover}, [role="button"]:focus-visible > &`]: {
    backgroundColor: colors.bgNeutralLight,
    color: colors.headingNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
      color: colors.headingNeutralDark,
    },
  },

  variants: {
    position: {
      start: {
        boxShadow: `inset -1px 0 0 ${colors.strokeNeutralLight}`,

        [darkThemeSelector]: {
          boxShadow: `inset -1px 0 0 ${colors.strokeNeutralDark}`,
        },
      },
      end: {
        boxShadow: `inset 1px 0 0 ${colors.strokeNeutralLight}`,

        [darkThemeSelector]: {
          boxShadow: `inset 1px 0 0 ${colors.strokeNeutralDark}`,
        },
      },
    },
  },
});
FlexiColumnsRail.displayName = 'FlexiColumnsRail';

export const FlexiColumnsRailLabel = styled(Text, {
  writingMode: 'vertical-rl',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: 0,
  color: 'inherit',
});
FlexiColumnsRailLabel.displayName = 'FlexiColumnsRailLabel';

/**
 * Width of the divider track. Exported so `FlexiColumns` can subtract it when working out how wide
 * a column was before it collapsed.
 */
export const FLEXI_COLUMNS_DIVIDER_WIDTH = rem(1);

/**
 * Every selector below reaches its parts through `>`, without exception. A nested `FlexiColumns`
 * renders this same component, so `&` matches the outer container as much as the inner one — a
 * descendant combinator here would let whichever container is further out decide whether the inner
 * one shows its divider and its overlay, and the inner one's own state would count for nothing.
 */
export const FlexiColumnsContainer = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '0',
  containerType: 'inline-size',
  containerName: containerNames.pane,

  [containers.pane.narrow]: {
    overflowX: 'hidden',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"start"',

    '&[data-active-column="start"] > [data-position="start"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="start"] > [data-position="end"]': {
      zIndex: flexiColumnsZIndexes.background,
      overflow: 'visible',
      transform: 'translateX(100%)',
    },
    '&[data-active-column="end"] > [data-position="end"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="end"] > [data-position="start"]': {
      zIndex: flexiColumnsZIndexes.foreground,
      overflow: 'visible',
      transform: 'translateX(-100%)',
    },

    '& > [data-flexi-overlay]': {
      display: 'block',
    },
    '& > [data-flexi-divider]': {
      display: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    display: 'grid',
    gridTemplateRows: '1fr',
  },

  '&[data-both-panes-visible="true"][data-resizable="true"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start divider end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: `var(--flexi-start-width) ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-start-width)`,
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: `var(--flexi-start-fr) ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-end-fr)`,
      },
    },
  },

  '&[data-both-panes-visible="true"][data-resizable="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start divider end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: `var(--flexi-fixed-start, $400) ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-fixed-end, $400)`,
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
    },
  },

  '&[data-end-overlay="true"][data-both-panes-visible="true"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start"',
      gridTemplateColumns: '1fr',

      '& > [data-position="end"]': {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: flexiColumnsZIndexes.foreground,
        width: 'min(var(--flexi-end-overlay-width), 100%)',
      },
      '& > [data-flexi-divider]': {
        position: 'absolute',
        top: 0,
        right: 'min(var(--flexi-end-overlay-width), 100%)',
        bottom: 0,
      },
    },
  },

  '&[data-both-panes-visible="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start end"',

      '&[data-active-column="start"]': {
        gridTemplateColumns: '1fr var(--flexi-peek-width-end)',
      },
      '&[data-active-column="end"]': {
        gridTemplateColumns: 'var(--flexi-peek-width-start) 1fr',
      },

      // The collapsed column holds the width it had before it collapsed, so the peek shows a slice
      // of the layout the user was just looking at. Left to size itself against the peek strip its
      // content reflows into a column a few characters wide — rows wrap, labels truncate — and the
      // sliver on show is of a layout that never existed. `max-content` isn't a substitute: content
      // that truncates or sets `min-width: 0` contributes almost nothing to it, so the very rows
      // most likely to reflow are the ones it fails to hold open.
      '&[data-active-column="end"] > [data-position="start"] > [data-flexi-slot="body"]': {
        width: 'var(--flexi-expanded-width-start)',
        minWidth: '100%',
      },
      '&[data-active-column="start"] > [data-position="end"] > [data-flexi-slot="body"]': {
        width: 'var(--flexi-expanded-width-end)',
        minWidth: '100%',
      },

      '& > [data-flexi-overlay]': {
        display: 'block',
      },
      '& > [data-flexi-divider]': {
        display: 'none',
      },
      // The rail draws this edge itself so it can fade out at the top.
      '& > [data-position]': {
        boxShadow: 'none',
      },
    },
  },

  '&[data-both-panes-visible="true"]': {
    [containers.pane.notNarrow]: {
      '& > [data-flexi-overlay]': {
        display: 'none',
      },
    },
  },

  '&[data-active-column="start"] > [data-flexi-overlay] [data-toggle="start"]': {
    display: 'none',
  },
  '&[data-active-column="end"] > [data-flexi-overlay] [data-toggle="end"]': {
    display: 'none',
  },
  '&[data-nested-active-column="end"] > [data-flexi-overlay] [data-toggle="start"]': {
    [containers.pane.narrow]: {
      display: 'none',
    },
  },

  variants: {
    root: {
      true: {
        containerName: `${containerNames.pane} ${FLEXI_COLUMNS_ROOT_CONTAINER_NAME}`,
      },
    },
  },
});
FlexiColumnsContainer.displayName = 'FlexiColumnsContainer';

export const FlexiColumnsOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.peek,
  pointerEvents: 'none',
  display: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },
});
FlexiColumnsOverlay.displayName = 'FlexiColumnsOverlay';

export const FlexiColumnsEdgeTabLayer = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.divider,
  pointerEvents: 'none',
  display: 'none',

  [containers.pane.narrow]: {
    display: 'block',
  },

  '& > *': {
    pointerEvents: 'auto',
  },
});
FlexiColumnsEdgeTabLayer.displayName = 'FlexiColumnsEdgeTabLayer';

export const FlexiColumnsControlsContainer = styled(UtilityControlGroup, {
  position: 'absolute',
  left: 0,
  transform: 'translateX(-50%)',

  [devices.touch]: {
    top: '$2',
  },

  [devices.mouse]: {
    top: '$8',
  },

  variants: {
    opaque: {
      true: {
        '&& > button': opaqueControlStyles,
      },
    },
  },
});
FlexiColumnsControlsContainer.displayName = 'FlexiColumnsControlsContainer';

export const FlexiColumnsDivider = styled('div', {
  gridArea: 'divider',
  position: 'relative',
  widthAll: FLEXI_COLUMNS_DIVIDER_WIDTH,
  zIndex: flexiColumnsZIndexes.divider,
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
FlexiColumnsDivider.displayName = 'FlexiColumnsDivider';
